import { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import axios from 'axios';
import Loader from './components/Loader';
import LayoutView from './modules/layout/LayoutView';
import lazyComponentLoader from './hoc/LazyLoader';
import ProtectedRoute from './hoc/ProtectedRoute';
import DialogUtils from './utils/DialogUtils';

interface AppError {
  message: string;
  code?: string | number;
}

const Login = lazyComponentLoader(() => import('./modules/auth/login/Login'));
const Forgot = lazyComponentLoader(() => import('./modules/auth/forgot-password/ForgotPassword'));
const Reset = lazyComponentLoader(() => import('./modules/auth/forgot-password/ResetPassword'));
const MerchantManagementView = lazyComponentLoader(() => import('./modules/merchant-management/MerchantManagementView'));
const PayablesManagementView = lazyComponentLoader(() => import('./modules/payable-management/PayableManagementView'));
const DeliveryPayablesManagementView = lazyComponentLoader(() => import('./modules/delivery-payable-management/DeliveryPayableManagementView'));
const OrderManagementView = lazyComponentLoader(() => import('./modules/orders-management/OrderManagementView'));
const VoucherManagementView = lazyComponentLoader(() => import('./modules/voucher-management/VoucherManagementView'));
const UnknownUrlViewView = lazyComponentLoader(() => import('./modules/unknown-url-management/UnknownUrlViewView'));
const SalesHistoryManagementView = lazyComponentLoader(() => import('./modules/sales-history-management/SalesHistoryView'));
const LocationManagementView = lazyComponentLoader(() => import('./modules/location-management/LocationManagementView'));
const CategoryManagementView = lazyComponentLoader(() => import('./modules/category-management/CategoryManagementView'));
// [HIDDEN] VIP Rewards / Loyalty Cards — temporarily hidden, do not delete
// const LoyaltySettingsView = lazyComponentLoader(() => import('./modules/loyalty-management/LoyaltySettingsView'));


const showError = (errors: AppError) => {
  const content = Array.isArray(errors) ? (
    <div>
      {errors.map((item: AppError) => (
        <div key={crypto.randomUUID()}>
          {item.message.includes('timeout') || item.message.includes('access_token')
            ? 'Your session has timed out.'
            : item.message}
        </div>
      ))}
    </div>
  ) : (
    <div>
      {errors.message?.includes('Network Error')
        ? 'No network connection. Make sure that WI-FI or Cellular Mobile Data is turned on, then try again.'
        : errors.message}
    </div>
  );
  DialogUtils.error(content);
};

const App = () => {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).closest('.ant-pagination .ant-select') &&
          !['ArrowUp', 'ArrowDown', 'Enter', 'Escape', 'Tab'].includes(e.key)) {
        e.preventDefault();
      }
    };
    document.addEventListener('keydown', handler, true);
    return () => document.removeEventListener('keydown', handler, true);
  }, []);

  useEffect(() => {
    // ✅ only handles loading state + error display
    // token refresh is handled in APIService
    const requestInterceptor = axios.interceptors.request.use(
      (config) => { setIsLoading(true); return config; },
      (error) => Promise.reject(error)
    );

    const responseInterceptor = axios.interceptors.response.use(
      (response) => { setIsLoading(false); return response; },
      async (error) => {
        setIsLoading(false);
        if (error.response) {
          showError(error.response.data.errors);
        } else {
          showError(error);
        }
        return Promise.reject(error);
      }
    );

    // ✅ cleanup prevents duplicate registration in StrictMode
    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  return (
    <div>
      <Routes>
        <Route path='/login' element={<Login />} />
        <Route path='/forgot-password' element={<Forgot />} />
        <Route path='/reset-password' element={<Reset />} />

        <Route element={<LayoutView />}>
          <Route index element={<ProtectedRoute element={<MerchantManagementView />} />} />
          <Route path='/merchants' element={<ProtectedRoute element={<MerchantManagementView />} />} />
          <Route path='/orders' element={<ProtectedRoute element={<OrderManagementView />} />} />
          <Route path='/merchant_payables' element={<ProtectedRoute element={<PayablesManagementView />} />} />
          <Route path='/delivery_payables' element={<ProtectedRoute element={<DeliveryPayablesManagementView />} />} />
          <Route path='/vouchers' element={<ProtectedRoute element={<VoucherManagementView />} />} />
          <Route path='/sales_reports' element={<ProtectedRoute element={<SalesHistoryManagementView />} />} />
          <Route path='/locations' element={<ProtectedRoute element={<LocationManagementView />} />} />
          <Route path='/categories' element={<ProtectedRoute element={<CategoryManagementView />} />} />
          {/* [HIDDEN] VIP Rewards / Loyalty Cards — temporarily hidden, do not delete */}
          {/* <Route path='/loyalty-settings' element={<ProtectedRoute element={<LoyaltySettingsView />} />} /> */}
          <Route path='/:link' element={<ProtectedRoute element={<UnknownUrlViewView />} />} />
        </Route>

        <Route path='*' element={<Navigate to='/' replace />} />
      </Routes>

      {isLoading ? <Loader /> : null}
    </div>
  );
};

export default App;

